import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { api } from '../api/client.ts';
import type { PlanMark } from '../api/types.ts';
import { usePlayground } from '../state/playground.ts';
import { useStore } from '../state/store.tsx';
import { markStillApplies, type MarkKind } from './marks.ts';

/** The active plan's Start markers, read once per plan and version and shared by every screen. */
const cache = new Map<string, readonly PlanMark[]>();
const loading = new Set<string>();
const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const NONE: readonly PlanMark[] = [];

/** Item id → the marker that still applies to it (an edited item has none). */
export function useMarks(): ReadonlyMap<string, MarkKind> {
  const { activePlanId, plan, version, auth } = useStore();
  const playground = usePlayground() !== null;
  const key = `${activePlanId ?? 'primary'}`;
  const marks = useSyncExternalStore(subscribe, () => cache.get(key) ?? NONE);
  useEffect(() => {
    if (playground || auth !== 'signedIn' || loading.has(key) || cache.has(`${key}@${version}`)) return;
    loading.add(key);
    api.marks(activePlanId)
      .then(({ marks: list }) => {
        cache.set(key, list);
        cache.set(`${key}@${version}`, list);
      })
      .catch(() => {})
      .finally(() => {
        loading.delete(key);
        for (const l of listeners) l();
      });
  }, [key, activePlanId, version, playground, auth]);
  return useMemo(() => {
    const out = new Map<string, MarkKind>();
    for (const m of marks) {
      const item = (plan?.transactions ?? []).find((t) => t.id === m.itemId);
      if (item && markStillApplies(item, m.fingerprint) && (out.get(m.itemId) !== 'example')) out.set(m.itemId, m.mark);
    }
    return out;
  }, [marks, plan]);
}
